import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Check, Stamp, AlertTriangle, Settings } from "lucide-react";
import api from "@/lib/api";

const ICON = { customs_cleared: Stamp, route_breach: AlertTriangle };

function playPing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = 880;
    g.gain.value = 0.05;
    o.connect(g); g.connect(ctx.destination);
    o.start();
    o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    o.stop(ctx.currentTime + 0.26);
    setTimeout(() => ctx.close(), 400);
  } catch { /* ignore */ }
}

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({});
  const [types, setTypes] = useState([]);
  const [cadence, setCadence] = useState("login");
  const [muteEnabled, setMuteEnabled] = useState(false);
  const [muteStart, setMuteStart] = useState(22);
  const [muteEnd, setMuteEnd] = useState(6);
  const [showPrefs, setShowPrefs] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  const seenRef = useRef(null);

  const isMuted = () => {
    if (!muteEnabled) return false;
    const h = new Date().getHours();
    if (muteStart === muteEnd) return true;
    return muteStart < muteEnd ? (h >= muteStart && h < muteEnd) : (h >= muteStart || h < muteEnd);
  };

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items);
      setUnread(r.data.unread_count);
      if (r.data.prefs) setPrefs(r.data.prefs);
      if (r.data.types) setTypes(r.data.types);
      if (r.data.bubble_cadence) setCadence(r.data.bubble_cadence);
      setMuteEnabled(!!r.data.mute_enabled);
      if (r.data.mute_start != null) setMuteStart(r.data.mute_start);
      if (r.data.mute_end != null) setMuteEnd(r.data.mute_end);
      const ids = r.data.items.map((i) => i.id);
      if (seenRef.current) {
        const fresh = r.data.items.filter((i) => !seenRef.current.has(i.id) && !i.read);
        if (fresh.length) {
          if (!isMuted()) playPing();
          toast(fresh.length === 1 ? fresh[0].title : `${fresh.length} new alerts`,
            { description: fresh.length === 1 ? fresh[0].body : undefined });
        }
      }
      seenRef.current = new Set(ids);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const markAll = async () => {
    try {
      await api.post("/notifications/read", {});
      setUnread(0);
      setItems((x) => x.map((i) => ({ ...i, read: true })));
    } catch { /* ignore */ }
  };

  const go = (n) => { if (n.link) nav(n.link); setOpen(false); };

  const putPrefs = async (extra = {}) => {
    const payload = { types: prefs, bubble_cadence: cadence, mute_enabled: muteEnabled, mute_start: muteStart, mute_end: muteEnd, ...extra };
    try { await api.put("/notifications/prefs", payload); load(); } catch { /* ignore */ }
  };
  const savePref = (key) => { const next = { ...prefs, [key]: !prefs[key] }; setPrefs(next); putPrefs({ types: next }); };
  const saveCadence = (v) => { setCadence(v); putPrefs({ bubble_cadence: v }); };
  const saveMute = (patch) => {
    if ("mute_enabled" in patch) setMuteEnabled(patch.mute_enabled);
    if ("mute_start" in patch) setMuteStart(patch.mute_start);
    if ("mute_end" in patch) setMuteEnd(patch.mute_end);
    putPrefs(patch);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell"
        className="relative p-2 rounded-lg hover:bg-muted transition-colors">
        <Bell size={20} />
        {unread > 0 && (
          <span data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-panel"
          className="absolute right-0 mt-2 w-[340px] max-h-[440px] overflow-y-auto scrollbar-thin rounded-xl border border-border bg-card shadow-2xl z-50">
          <div className="sticky top-0 bg-card border-b border-border">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="font-heading font-bold text-sm">Alerts</span>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowPrefs((s) => !s)} data-testid="notif-prefs-toggle" className="text-muted-foreground hover:text-foreground"><Settings size={15} /></button>
                <button onClick={markAll} data-testid="notif-mark-read" className="text-xs text-primary font-semibold flex items-center gap-1 hover:underline"><Check size={13} /> Mark all read</button>
              </div>
            </div>
            {showPrefs && (
              <div className="px-4 pb-3 space-y-2" data-testid="notif-prefs">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Ring my bell for</p>
                {types.map((t) => (
                  <label key={t.key} className="flex items-center justify-between text-sm cursor-pointer">
                    <span>{t.label}</span>
                    <input type="checkbox" checked={!!prefs[t.key]} onChange={() => savePref(t.key)} data-testid={`notif-pref-${t.key}`} />
                  </label>
                ))}
                <div className="pt-2 mt-1 border-t border-border">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Roadman greeting</p>
                  <select value={cadence} onChange={(e) => saveCadence(e.target.value)} data-testid="notif-bubble-cadence"
                    className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm">
                    <option value="login">Every login</option>
                    <option value="daily">Once a day</option>
                  </select>
                </div>
                <div className="pt-2 mt-1 border-t border-border">
                  <label className="flex items-center justify-between text-sm cursor-pointer mb-1.5">
                    <span>Quiet hours (mute ping)</span>
                    <input type="checkbox" checked={muteEnabled} onChange={(e) => saveMute({ mute_enabled: e.target.checked })} data-testid="notif-mute-enabled" />
                  </label>
                  {muteEnabled && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">From</span>
                      <select value={muteStart} onChange={(e) => saveMute({ mute_start: Number(e.target.value) })} data-testid="notif-mute-start" className="h-7 rounded border border-border bg-background px-1">
                        {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                      </select>
                      <span className="text-muted-foreground">to</span>
                      <select value={muteEnd} onChange={(e) => saveMute({ mute_end: Number(e.target.value) })} data-testid="notif-mute-end" className="h-7 rounded border border-border bg-background px-1">
                        {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {items.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No alerts yet — all sharp.</p>}
          {items.map((n) => {
            const I = ICON[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => go(n)} data-testid={`notif-item-${n.id}`}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border hover:bg-muted transition-colors ${!n.read ? "bg-accent/40" : ""}`}>
                <span className={`mt-0.5 shrink-0 ${n.type === "route_breach" ? "text-orange-500" : "text-primary"}`}><I size={16} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold truncate">{n.title}</span>
                  <span className="block text-xs text-muted-foreground">{n.body}</span>
                  <span className="block text-[10px] text-muted-foreground mt-0.5 font-mono">
                    {new Date(n.created_at).toLocaleString("en-ZA", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </span>
                {!n.read && <span className="ml-1 w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
